<template>
  <AntRow>
    <AntCol :span="24">
      <div v-if="isDev" style="display: flex;">
        <DevDebugDataBlock
          title="customizedVersionSelectedDocIdByFieldId"
          :data="customizedVersionSelectedDocIdByFieldId"
          width="50%"
          maxHeight="250px"
        />
        <DevDebugDataBlock
          title="dataSource"
          :data="dataSource"
          width="50%"
          maxHeight="250px"
        />
      </div>

      <div
        v-if="addibleColumn"
        class="add-column"
      >
        <AntButton
          type="primary"
          size="small"
          @click="onAddColumn"
        >
          <AddIcon /> Add Column
        </AntButton>
      </div>

      <AntTable
        ref="tableRootRef"
        class="table-data-table"
        :columns="tableColumns"
        :data-source="dataSource"
        :loading="isTableLoading"
        :pagination="false"
        :scroll="{ x: 'max-content', y: 700 }"
        :custom-row="customRow"
        bordered
        default-expand-all-rows
        expand-fixed="right"
      >
        <template #headerCell="{ title, column }">
          <template v-if="column.dataIndex !== 'sort' && column.dataIndex !== 'action'">
            <template v-if="column.columnTitleElement">
              <div :class="[!selectedSectionState.tableColumns[column.dataIndex].columnTitle && 'ant-form-item-has-error']">

                <div v-if="isDev" style="font-size: 11px; color: #bbb;">dataIndex: {{ column.dataIndex }}</div>

                <AntPopconfirm
                  v-if="addibleColumn && selectedSectionState.tableColumns[column.dataIndex]?.sortOrder !== 0"
                  title="Sure to delete this column?"
                  @confirm="onDeleteColumn(column.dataIndex)"
                >
                  <span class="delete-column-btn">
                    <CloseCircleIcon /> Delete Column
                  </span>
                </AntPopconfirm>

                <div
                  :data-column-title-input="column.dataIndex"
                  style="display: flex; align-items: center; gap: 4px;"
                >
                  <AntTextarea
                    v-if="column.columnTitleElement === 'textarea'"
                    :value="selectedSectionState.tableColumns[column.dataIndex].columnTitle"
                    style="width: 100%;"
                    @change="e => onChangeColumnTitle(column.dataIndex, e.target.value)"
                  />
                  <AntSelect
                    v-if="column.columnTitleElement === 'select'"
                    :value="selectedSectionState.tableColumns[column.dataIndex].columnTitle"
                    placeholder="Select API"
                    style="width: 100%;"
                    :options="addibleColumn.options"
                    show-search
                    @change="val => onChangeColumnTitle(column.dataIndex, val)"
                  />
                  <SortIcon
                    v-if="column.dataIndex === 'platform'"
                    class="sort-icon"
                    title="按字母排序"
                    @click="onSortPlatformColumn"
                  />
                </div>
                <ul v-if="!isReleaseVer && selectedSectionState.tableColumns[column.dataIndex].sortOrder === 0" class="docs-setting-desc-list">
                  <li>Used In：需顯示在哪本 API 文件中</li>
                  <li>Not Used In：表示不顯示在該 API 文件中，當 Used In 為 All 時才可設定</li>
                  <li>Version：可切換檢視或新增刪除不同的 API 文件客製內容版本</li>
                </ul>
                <div
                  v-if="!selectedSectionState.tableColumns[column.dataIndex].columnTitle"
                  class="ant-form-item-explain-error"
                >
                  Required
                </div>
              </div>
            </template>
          </template>

          <template v-else>
            {{ title }}
          </template>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'sort'">
            <DragIcon
              v-if="!record.isNew"
              style="cursor: move; color: #999"
              @mouseenter="onMouseEnterDrag"
            />
          </template>

          <template v-else-if="column.dataIndex === 'action'">
            <AntPopconfirm
              v-if="!record.isNew"
              :title="`Sure to delete?`"
              @confirm="onDeleteRow(record)"
            >
              <DeleteIcon title="Delete" />
            </AntPopconfirm>
          </template>

          <template v-else>
            <div
              v-if="isDev && selectedSectionState.tableColumns[column.dataIndex]?.sortOrder === 0"
              style="font-size: 11px; color: #bbb;"
            >
              <template v-if="!record.docId?.includes('master')">docId: {{ record.docId }}<br></template>
              key: {{ record.key }}<br>
              sortOrder: {{ record.sortOrder }}<br>
              <template v-if="record.isNew">isNew: {{ record.isNew }}</template>
              <template v-if="record.added">added: {{ record.added }}</template>
            </div>

            <div
              v-if="column.element === 'editor'"
              class="field-section-type"
            >
              <component
                :is="getComponent(column)"
                v-bind="getComponentProps(record, column)"
              />
            </div>

            <template v-else-if="selectedSectionState.tag === 'list-of-platforms' && column.dataIndex === 'column_2'">
              <!-- column_1: ['EGAME', 'SLOT'] -->
              <!-- column_2: { EGAME: false, SLOT: false } -->
              <div
                v-if="column.element === 'switch'"
                class="object-group"
              >
                <div
                  v-for="gameType in getFieldStateKey(record.fieldId, 'column_1')"
                  :key="gameType"
                >
                  <component
                    :is="getComponent(column)"
                    v-bind="getComponentProps(record, column, gameType)"
                  />
                  <span class="switch-label">{{ gameType }}</span>
                </div>
              </div>

              <div
                v-else
                class="object-group"
              >
                <div
                  v-for="key in Object.keys(record[column.dataIndex])"
                  :key="key"
                >
                  <label v-if="isReleaseVer">{{ `${titleCase(key)} environment:` }}</label>
                  <label v-else>{{ titleCase(key) }}</label>
                  <component
                    :is="getComponent(column)"
                    v-bind="getComponentProps(record, column, key)"
                  />
                </div>
              </div>
              <template v-if="isReleaseVer">
                <br>
                <div
                  v-for="[key, val] in Object.entries(record[column.dataIndex])"
                  :key="key"
                >
                  <b>{{ key === 'staging' ? '测试' : '正式' }}环境</b>：{{ val.toLowerCase() === 'already released' ? '已上线' : val }}
                </div>
              </template>
            </template>

            <div
              v-else-if="column.element"
              :data-table-cell-input="`${record.fieldId}__${column.dataIndex}`"
              :class="[column.required && !record.isNew && (!record[column.dataIndex] || !record[column.dataIndex]?.length) && 'ant-form-item-has-error']"
            >
              <component
                :is="getComponent(column)"
                v-bind="getComponentProps(record, column)"
                :disabled="column.dataIndex !== 'platform' && !selectedSectionState.tableColumns[column.dataIndex].columnTitle"
              />
              <div
                v-if="column.required && !record.isNew && (!record[column.dataIndex] || !record[column.dataIndex]?.length)"
                class="ant-form-item-explain-error"
              >
                Required
              </div>
              <div
                v-if="column.pattern && !record.isNew && !column.pattern.test(record[column.dataIndex])"
                class="ant-form-item-explain-error"
              >
                {{ column.errorMessage || 'Invalid format' }}
              </div>
            </div>

            <span v-else>{{ record[column.dataIndex] }}</span>

            <div v-if="!isReleaseVer && selectedSectionState.tableColumns[column.dataIndex]?.sortOrder === 0 && !record.isNew" style="margin-left: 20px;">
              <div class="docs-setting-row">
                <label class="table-label">Used In:</label>
                <AntTooltip
                  :overlayClassName="customizedVersionSelectedDocIdByFieldId[record.fieldId] ? '' : 'hide-tooltip'"
                  title="請切回 General 版本編輯"
                  placement="topLeft"
                >
                  <PlatformDocSelect
                    type="docs"
                    :value="record.docs"
                    :options="selectedSectionState.docs?.map(docId => ({ label: docsDataByDocId[docId].docShortTitle, value: docId }))"
                    @change="val => onChange(val ? sortDocIds(val) : null, 'docs', null, record)"
                    :disabled="!!customizedVersionSelectedDocIdByFieldId[record.fieldId]"
                  />
                </AntTooltip>
              </div>

              <div v-if="!record.docs" class="docs-setting-row">
                <label class="table-label">Not Used In:</label>
                <AntTooltip
                  :overlayClassName="customizedVersionSelectedDocIdByFieldId[record.fieldId] ? '' : 'hide-tooltip'"
                  title="請切回 General 版本編輯"
                  placement="topLeft"
                >
                  <PlatformDocSelect
                    type="excludedDocs"
                    :value="record.excludedDocs"
                    :options="selectedSectionState.docs?.map(docId => ({ label: docsDataByDocId[docId].docShortTitle, value: docId }))"
                    @change="val => onChange(val ? sortDocIds(val) : null, 'excludedDocs', null, record)"
                    :disabled="!!customizedVersionSelectedDocIdByFieldId[record.fieldId]"
                  />
                </AntTooltip>
              </div>

              <div class="docs-setting-row">
                <label class="table-label">Version:</label>
                <CustomizedVersionSelect
                  size="small"
                  :selectedDocId="customizedVersionSelectedDocIdByFieldId[record.fieldId]"
                  :docsDataByDocId="docsDataByDocId"
                  :selectedSectionState="selectedSectionState"
                  :selectedSectionOrFieldState="fieldState[record.fieldId] || {}"
                  :dropdownStyle="{ width: '500px' }"
                  @update:selectedDocId="val => { customizedVersionSelectedDocIdByFieldId[record.fieldId] = val === 'general' ? undefined : val }"
                />
              </div>
            </div>
          </template>
        </template>
      </AntTable>
    </AntCol>
  </AntRow>
</template>

<script>
import {
  ref,
  reactive,
  inject,
  // watch,
  computed,
  nextTick,
  // watchEffect,
  // onMounted,
} from 'vue'

import ShortUniqueId from 'short-unique-id'

import { cloneDeep } from 'lodash'

import {
  Row as AntRow,
  Col as AntCol,
  Table as AntTable,
  Input as AntInput,
  Button as AntButton,
  Select as AntSelect,
  Switch as AntSwitch,
  Tooltip as AntTooltip,
  AutoComplete as AntAutoComplete,
  Popconfirm as AntPopconfirm,
  message as antMessage,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import EditIcon from '@ant-design/icons-vue/lib/icons/EditOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'
import CloseCircleIcon from '@ant-design/icons-vue/lib/icons/CloseCircleOutlined'
import DragIcon from '@ant-design/icons-vue/lib/icons/HolderOutlined'
import SortIcon from '@ant-design/icons-vue/lib/icons/SortAscendingOutlined'
import QuestionIcon from '@ant-design/icons-vue/lib/icons/QuestionCircleOutlined'

import SectionEditor from '@/components/ApiDoc/editor/SectionEditor.vue'
import CustomizedVersionSelect from '@/components/ApiDoc/editor/CustomizedVersionSelect.vue'
import DevDebugDataBlock from '@/components/ApiDoc/shared/DevDebugDataBlock.vue'
import PlatformDocSelect from '@/components/ApiDoc/shared/PlatformDocSelect.vue'

import { titleCase } from '@/utils'
import { sortDocIds } from '../lib/libsV3'

import {
  clearTableRowDragIndicator,
  updateTableRowDragIndicator,
  getDropPositionFromRow,
  reorderRowsWithDropPosition,
} from './tableRowDragSort'

import './tableRowDragSort.css'

export default {
  name: 'TableDataTable',
  components: {
    DevDebugDataBlock,
    SectionEditor,
    PlatformDocSelect,
    CustomizedVersionSelect,
    AntRow,
    AntCol,
    AntTable,
    AntInput,
    AntButton,
    AntTextarea: AntInput.TextArea,
    AntSelect,
    AntSelectOption: AntSelect.Option,
    AntSwitch,
    AntAutoComplete,
    AntPopconfirm,
    AntTooltip,
    AddIcon,
    EditIcon,
    DeleteIcon,
    CloseCircleIcon,
    DragIcon,
    SortIcon,
    QuestionIcon,
    VNodes: (_, { attrs }) => attrs.vnodes,
  },
  props: {
    isTableLoading: Boolean,
    imageManagementData: Array,
    sectionsDataByDocId: Object,
    customizedVersionSelectedDocId: String,
    docsDataByDocId: Object,
    selectedSectionState: Object,
    selectedFieldState: Object,
    columns: {
      type: Array,
      default: () => [],
    },
    addibleColumn: Object,
    platform: String,
    disableSort: Boolean,
    /** 'end' 新列在表格最後（預設）；'start' 新列在 body 第一行；'none' 不顯示新列 */
    newRowPosition: {
      type: String,
      default: 'end',
      validator: v => ['end', 'start', 'none'].includes(v),
    },
  },
  setup(props, { expose }) {
    const isDev = inject('isDev')
    const isReleaseVer = computed(() => props.selectedSectionState.sectionId === 'ReleaseVer')
    const pendingTableCellErrorFocusKey = ref(null)

    const showSearchText = ref('')
    const searchState = reactive({})

    const selectedSectionState = reactive(props.selectedSectionState)
    const selectedFieldState = reactive(props.selectedFieldState)

    if (!selectedFieldState.row) {
      selectedFieldState.row = {}
    }

    const fieldState = computed(() => selectedFieldState.row || {})

    const numberTypeOptions = ['integer', 'float'].map(i => ({ label: i, value: i }))

    const tableColumns = computed(() => {
      const baseColumns = Array.isArray(props.columns) ? props.columns : []
      return [
        ...(props.disableSort ? [] : [{
          title: '',
          dataIndex: 'sort',
          key: 'sort',
          width: 30,
          fixed: 'left',
          align: 'center',
        }]),
        baseColumns[0],
        // ...(isReleaseVer.value ? [] : [
        //   {
        //     title: 'Used In',
        //     dataIndex: 'docs',
        //     key: 'docs',
        //     width: 200,
        //   },
        // ]),
        ...baseColumns.slice(1),
        {
          title: 'Action',
          dataIndex: 'action',
          key: 'action',
          width: 45,
          align: 'center',
          fixed: 'right',
        },
      ].filter(Boolean)
    })

    const appendNewRowToDataSource = (rows) => {
      const newFieldId = new ShortUniqueId({ length: 10 }).rnd()
      const { sectionId, wallet } = selectedSectionState
      const newRow = {
        wallet,
        fieldId: newFieldId,
        key: `${sectionId}#row#${newFieldId}`,
        isNew: true,
        scope: 'row',
        sortOrder: props.newRowPosition === 'start' ? 0 : rows.length,
        ...(props.platform && { platform: props.platform }),
      }
      if (props.newRowPosition === 'start') rows.unshift(newRow)
      else rows.push(newRow)
    }

    const isNewRowPlaceholderIndex = (index, rows) => {
      if (props.newRowPosition === 'none') return false
      const row = rows[index]
      if (!row?.isNew) return false
      if (props.newRowPosition === 'start') return index === 0
      return index === rows.length - 1
    }

    const dataSource = computed(() => {
      console.log('[TableDataTable] computed dataSource', Object.keys(fieldState.value))
      const result = Object.values(fieldState.value).sort((a, b) => a.sortOrder - b.sortOrder).reduce((acc, d) => {
        // if (isPlatformTable.value && !platformsMap.value[d.platform]) return acc
        if (d.deleted) return acc
        if (props.platform && props.platform !== d.platform) return acc

        acc.push({
          ...d,
          key: d.sectionIdFieldId,
        })
        return acc
      }, [])

      if (props.newRowPosition !== 'none') appendNewRowToDataSource(result)
      return result
    })

    const customizedVersionSelectedDocIdByFieldId = reactive({})
    const getFieldStateKey = (fieldId, key) => fieldState.value[fieldId]?.customizedVersion?.[customizedVersionSelectedDocIdByFieldId[fieldId]]?.[key] || fieldState.value[fieldId]?.[key] || null

    // const isPlatformTable = computed(() => selectedSectionState.tableColumns?.platform)

    // const updateRowDataAndDataSourceSortOrder = () => {
    //   const newDataSource = []
    //   rowData.value = rowData.value
    //     .map((d, idx) => {
    //       if (!isPlatformTable.value || platformsMap.value[d.platform]) {
    //         newDataSource.push({ ...d, key: d.sectionIdFieldId, sortOrder: idx })
    //       }
    //       return { ...d, sortOrder: idx }
    //     })

    //   dataSource.value = newDataSource
    //   if (props.newRowPosition !== 'none') appendNewRowToDataSource()
    // }

    // onMounted(() => {
    //   console.log('[TableDataTable] onMounted', tableData.value)
    //   updateRowDataAndDataSourceSortOrder()
    //   emit('change', tableData.value)
    // })

    const onChangeColumnTitle = (dataIndex, val) => {
      console.log('[TableDataTable] onChangeColumnTitle dataIndex, val', dataIndex, val)
      selectedSectionState.tableColumns[dataIndex].columnTitle = val
    }

    const onSortPlatformColumn = () => {
      dataSource.value = dataSource.value.sort((a, b) => {
        if (a.platform && !b.platform) return -1 // a 有 platform, b 沒有 => a 排前面
        if (!a.platform && b.platform) return 1 // a 沒有 platform, b 有 => b 排前面
        if (a.platform && b.platform) return a.platform?.localeCompare(b.platform) // 兩個都有 (或都沒有) platform，排序
        return 0 // 兩個都沒有 platform，不動順序
      })

      dataSource.value.forEach(({ fieldId }, idx) => {
        if (!fieldState.value[fieldId]) return // isNew
        fieldState.value[fieldId].sortOrder = idx
      })

      console.log('[TableDataTable] onSortPlatformColumn', dataSource.value)
      antMessage.success('已將 Platform 按字母排序')
    }

    const tableRootRef = ref(null)

    const getTableRootEl = () => tableRootRef.value?.$el || tableRootRef.value

    const scrollTableToEnd = (root) => {
      if (!root?.querySelectorAll) return
      root.querySelectorAll('.ant-table-body, .ant-table-header, .ant-table-content').forEach((el) => {
        el.scrollLeft = el.scrollWidth
      })
    }

    const isVisibleEl = (el) => {
      if (!el) return false
      const rect = el.getBoundingClientRect()
      if (rect.width < 2 || rect.height < 2) return false
      const style = window.getComputedStyle(el)
      return style.visibility !== 'hidden' && style.display !== 'none'
    }

    const tryFocusVisibleColumnTitleInput = (root, dataIndex) => {
      if (!root?.querySelectorAll) return false
      const holders = [
        ...(root.querySelector('.ant-table-header')?.querySelectorAll(`[data-column-title-input="${dataIndex}"]`) || []),
        ...root.querySelectorAll(`[data-column-title-input="${dataIndex}"]`),
      ]
      const seen = new Set()
      for (const holder of holders) {
        if (seen.has(holder)) continue
        seen.add(holder)
        const textarea = holder.querySelector('textarea')
        if (textarea && isVisibleEl(textarea)) {
          textarea.focus({ preventScroll: true })
          if (typeof textarea.select === 'function') textarea.select()
          return document.activeElement === textarea
        }
        const selector = holder.querySelector('.ant-select-selector')
        if (selector && isVisibleEl(selector)) {
          selector.click()
          const searchInput = holder.querySelector('input:not([type="hidden"])')
          if (searchInput) searchInput.focus({ preventScroll: true })
          return true
        }
      }
      return false
    }

    const focusColumnTitleInput = async (dataIndex, maxMs = 2000) => {
      const deadline = Date.now() + maxMs
      let didScroll = false
      while (Date.now() < deadline) {
        const root = getTableRootEl()
        if (root?.querySelector?.(`[data-column-title-input="${dataIndex}"]`)) {
          if (!didScroll) {
            scrollTableToEnd(root)
            didScroll = true
            await nextTick()
            await new Promise(resolve => requestAnimationFrame(resolve))
            await new Promise(resolve => setTimeout(resolve, 50))
            scrollTableToEnd(getTableRootEl())
          }
          if (tryFocusVisibleColumnTitleInput(getTableRootEl(), dataIndex)) return true
        }
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return false
    }

    const onAddColumn = async () => {
      const columnKeys = Object.keys(selectedSectionState.tableColumns || {})
      console.log('[TableDataTable] onAddColumn columnKeys', columnKeys)
      const lastColumnNo = columnKeys.filter(key => key.startsWith('column_')).map(key => Number(key.split('_')[1])).sort((a, b) => a - b).pop() || 0
      console.log('[TableDataTable] onAddColumn lastColumnNo', lastColumnNo)
      const newColumnId = `column_${lastColumnNo + 1}`
      console.log('[TableDataTable] onAddColumn newColumnId', newColumnId)

      if (!selectedSectionState.tableColumns) selectedSectionState.tableColumns = {}
      selectedSectionState.tableColumns[newColumnId] = {
        columnId: newColumnId,
        columnTitleElement: props.addibleColumn.element,
        sortOrder: columnKeys.length,
        element: 'textarea',
      }
      await focusColumnTitleInput(newColumnId)
    }

    const onDeleteColumn = (dataIndex) => {
      const columns = selectedSectionState.tableColumns
      if (!columns?.[dataIndex] || columns[dataIndex].sortOrder === 0) return

      delete columns[dataIndex]

      Object.values(columns)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .forEach((col, idx) => {
          col.sortOrder = idx
        })

      const stripColumnValue = (target) => {
        if (!target || typeof target !== 'object' || Array.isArray(target)) return
        delete target[dataIndex]
      }

      Object.values(fieldState.value || {}).forEach((row) => {
        stripColumnValue(row)
        Object.values(row?.customizedVersion || {}).forEach(stripColumnValue)
      })
    }

    /** 刪除後將未刪除列的 sortOrder 重編為 0..n-1（與 FieldTable / 後端 reorder 一致） */
    const compactRowSortOrders = () => {
      Object.values(fieldState.value)
        .filter(f => f && !f.deleted)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .forEach((field, idx) => {
          field.sortOrder = idx
        })
    }

    const onDeleteRow = async (record) => {
      const { fieldId, added } = record
      if (added) {
        delete fieldState.value[fieldId]
      } else if (fieldState.value[fieldId]) {
        fieldState.value[fieldId].deleted = true
      }
      compactRowSortOrders()
    }

    const onMouseEnterDrag = (e) => {
      console.log('onMouseEnterDrag e', e)
      const rowParent = e.target.closest('.ant-table-row')
      rowParent.draggable = true
    }

    const dragObj = ref({})
    const dropObj = ref({})
    const customRow = (record, index) => {
      const onDragstart = (e) => {
        e.stopPropagation()
        dragObj.value = record
        e.dataTransfer.effectAllowed = 'move'
        console.log('onDragstart', record.idx)
      }
      const onDragover = (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (record.isNew || isNewRowPlaceholderIndex(index, dataSource.value)) return
        updateTableRowDragIndicator(e.currentTarget, e)
      }
      const onDragenter = (e) => {
        e.preventDefault()
        if (record.isNew || isNewRowPlaceholderIndex(index, dataSource.value)) return
        updateTableRowDragIndicator(e.currentTarget, e)
      }
      const onDragleave = (e) => {
        const row = e.currentTarget
        if (!row.contains(e.relatedTarget)) clearTableRowDragIndicator(row)
      }
      const onDragend = (e) => {
        clearTableRowDragIndicator(e.currentTarget)
      }
      const onDrop = (e) => {
        e.stopPropagation()
        console.log('onDrop e', e, 'dropObj', record, 'dragObj', dragObj.value)
        dropObj.value = record
        const row = e.currentTarget
        clearTableRowDragIndicator(row)

        if (isNewRowPlaceholderIndex(index, dataSource.value)) return

        const rows = dataSource.value.filter(r => !r.isNew)
        const fromIndex = rows.findIndex(r => r.fieldId === dragObj.value.fieldId)
        const targetIndex = rows.findIndex(r => r.fieldId === record.fieldId)
        if (fromIndex === -1 || targetIndex === -1) return

        const position = getDropPositionFromRow(row)
        const reordered = reorderRowsWithDropPosition(rows, fromIndex, targetIndex, position)
        if (reordered === rows) return

        reordered.forEach((rowItem, idx) => {
          const { fieldId } = rowItem
          if (!fieldState.value[fieldId]) return // isNew
          fieldState.value[fieldId].sortOrder = idx
        })
      }

      return {
        onDragstart,
        onDragover,
        onDrop,
        onDragenter,
        onDragleave,
        onDragend,
      }
    }

    /** list-of-platforms：選取 Game Type 時，為 column_2 初始化對應 switch 預設值 false */
    const initColumn2ForGameTypes = (record, fieldData, gameTypes) => {
      if (!Array.isArray(gameTypes)) return
      if (!record.column_2) record.column_2 = {}
      if (!fieldData.column_2) fieldData.column_2 = {}
      gameTypes.forEach((gameType) => {
        if (record.column_2[gameType] == null) record.column_2[gameType] = false
        if (fieldData.column_2[gameType] == null) fieldData.column_2[gameType] = false
      })
    }

    const onChange = (val, key, nestKey, record) => {
      console.log('[TableDataTable] onChange', {
        val, key, nestKey, isNew: record.isNew, record,
      })

      const { fieldId } = record
      const selectedDocId = customizedVersionSelectedDocIdByFieldId[fieldId]
      if (!fieldState.value[fieldId]) {
        fieldState.value[fieldId] = {
          fieldId,
          sortOrder: record.sortOrder,
        }

        if (selectedDocId && selectedDocId !== 'general') {
          fieldState.value[fieldId].customizedVersion[selectedDocId] = {}
        }
      }

      const fieldData = selectedDocId && selectedDocId !== 'general'
        ? fieldState.value[fieldId].customizedVersion[selectedDocId]
        : fieldState.value[fieldId]

      if (nestKey) {
        if (!record[key]) record[key] = {}
        if (!fieldData[key]) fieldData[key] = {}
        record[key][nestKey] = val
        fieldData[key][nestKey] = val
      } else {
        record[key] = val
        fieldData[key] = val
      }

      if (key === 'column_1' && selectedSectionState.tag === 'list-of-platforms') {
        initColumn2ForGameTypes(record, fieldData, val)
      }

      if (record.isNew) {
        delete record.isNew

        const { sectionId, docId } = selectedSectionState
        const sectionIdFieldId = `${sectionId}#row#${fieldId}`
        record.sectionIdFieldId = sectionIdFieldId
        record.key = sectionIdFieldId
        record.sectionId = sectionId
        record.docId = docId
        record.parentFieldId = 'ROOT'
        record.level = 0
        if (isReleaseVer.value && !record.wallet) {
          record.wallet = props.docsDataByDocId?.[docId]?.wallet
        }

        const rowItem = cloneDeep(record)
        delete rowItem.key

        const sortOrder = props.newRowPosition === 'start' ? 0 : (rowItem.sortOrder ?? 0)
        if (props.newRowPosition === 'start') {
          Object.values(fieldState.value).forEach((f) => {
            if (!f || f.fieldId === fieldId || f.deleted) return
            if (typeof f.sortOrder === 'number') f.sortOrder += 1
          })
        }

        fieldState.value[fieldId] = {
          ...rowItem,
          sortOrder,
          added: true,
        }
      }
    }

    const getComponent = (column) => {
      if (column.element === 'editor') return SectionEditor
      return `ant-${column.element}`
    }

    const getComponentProps = (record, column, nestKey) => {
      const defaultOnChange = (e, val) => {
        console.log('[TableDataTable] defaultOnChange val', { e, val })

        let value
        if (['checkbox', 'switch', 'editor', 'select'].includes(column.element)) {
          value = e
        } else {
          value = val || e.target.value
        }

        if (column.onChangeCallback) column.onChangeCallback(value, column.dataIndex, nestKey, record)
        onChange(value, column.dataIndex, nestKey, record)
      }
      const { title, element, ...columnRest } = column
      const placeholder = (title || '').replaceAll('\n', ' ')

      const value = nestKey ? getFieldStateKey(record.fieldId, column.dataIndex)?.[nestKey] : getFieldStateKey(record.fieldId, column.dataIndex)

      const baseProps = {
        ...columnRest,
        ...(['checkbox', 'switch'].includes(element) ? {
          checked: value,
          // checked: nestKey ? record[column.dataIndex]?.[nestKey] : record[column.dataIndex],
        } : {
          value,
          // value: nestKey ? record[column.dataIndex]?.[nestKey] : record[column.dataIndex],
        }),
        onChange: defaultOnChange,
      }

      if (element === 'select' && column.mode === 'multiple' && !value) {
        baseProps.value = []
      }

      switch (element) {
        case 'input':
          return {
            ...baseProps,
            placeholder,
            style: { width: '100%' },
          }

        case 'textarea':
          return {
            ...baseProps,
            placeholder,
            style: { width: '100%' },
            autoSize: { minRows: 2, maxRows: 5 },
          }

        case 'select':
          if (column.sortValue) {
            baseProps.onChange = (val) => {
              if (!Array.isArray(val)) return
              const sortedVal = val.sort()
              defaultOnChange(sortedVal)
            }
          }

          return {
            ...baseProps,
            placeholder,
            style: {
              width: '100%',
              ...(selectedSectionState.tableColumns[column.dataIndex].sortOrder === 0 ? { maxWidth: '200px' } : {}),
            },
          }

        case 'autoComplete':
          return {
            ...baseProps,
            placeholder,
            style: { width: '100%' },
          }

        case 'editor':
          return {
            html: true,
            height: column.height || '250px',
            previewMode: true,
            removedToolbarItems: ['image'],
            imageManagementData: props.imageManagementData,
            sectionsDataByDocId: props.sectionsDataByDocId,
            customizedVersionSelectedDocId: props.customizedVersionSelectedDocId,
            sectionState: record,
            editorContent: getFieldStateKey(record.fieldId, column.dataIndex),
            onChange: defaultOnChange,
          }

        default:
          return baseProps
      }
    }

    const getCellValidationErrorType = (record, column) => {
      if (!record || record.isNew || record.deleted || !column?.element) return null
      const value = record[column.dataIndex]
      if (column.required && (!value || !value?.length)) return 'required'
      if (column.pattern && !column.pattern.test(value)) return 'pattern'
      return null
    }

    const findFirstTableCellError = (rows = dataSource.value, columns = props.columns) => {
      const cols = (Array.isArray(columns) ? columns : []).filter(column => (
        column?.element
        && column.dataIndex
        && column.dataIndex !== 'sort'
        && column.dataIndex !== 'action'
      ))

      const sortedRows = [...(rows || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      for (const record of sortedRows) {
        for (const column of cols) {
          const errorType = getCellValidationErrorType(record, column)
          if (!errorType) continue
          return {
            fieldId: record.fieldId,
            dataIndex: column.dataIndex,
            errorType,
            platform: record.platform,
            record,
            column,
          }
        }
      }
      return null
    }

    const checkTableCellValidationError = () => !!findFirstTableCellError()

    const getTableCellInputKey = (fieldId, dataIndex) => `${fieldId}__${dataIndex}`

    const isVisibleFocusableInput = (input) => {
      if (!input || input.disabled || input.readOnly) return false
      const rect = input.getBoundingClientRect()
      if (!rect.width || !rect.height) return false
      const style = window.getComputedStyle(input)
      return style.visibility !== 'hidden' && style.display !== 'none'
    }

    const getTableCellErrorInput = (fieldId, dataIndex) => {
      const key = getTableCellInputKey(fieldId, dataIndex)
      const holders = document.querySelectorAll(`[data-table-cell-input="${key}"]`)
      for (const holder of holders) {
        const candidates = [
          ...holder.querySelectorAll('input:not([type="hidden"]), textarea'),
          ...holder.querySelectorAll('.ant-select-selection-search-input'),
          ...holder.querySelectorAll('.ant-select-selector'),
        ]
        for (const candidate of candidates) {
          if (isVisibleFocusableInput(candidate)) return candidate
        }
        if (isVisibleFocusableInput(holder)) return holder
      }
      return null
    }

    const tryFocusTableCellErrorInput = (errOrKey) => {
      const key = typeof errOrKey === 'string'
        ? errOrKey
        : getTableCellInputKey(errOrKey.fieldId, errOrKey.dataIndex)
      const [fieldId, dataIndex] = key.split('__')
      const input = getTableCellErrorInput(fieldId, dataIndex)
      if (!input) return false

      const row = input.closest('tr.ant-table-row') || input.closest('tr')
      if (row) row.scrollIntoView({ block: 'center', behavior: 'smooth' })

      if (input.classList.contains('ant-select-selector')) {
        input.click()
        const searchInput = input.querySelector('input') || input.parentElement?.querySelector('input')
        if (searchInput) searchInput.focus({ preventScroll: true })
      } else {
        input.focus({ preventScroll: true })
        if (typeof input.select === 'function') input.select()
      }
      return true
    }

    const waitForTableCellErrorInput = async (err, maxMs = 2000) => {
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        const input = getTableCellErrorInput(err.fieldId, err.dataIndex)
        if (input) return input
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return getTableCellErrorInput(err.fieldId, err.dataIndex)
    }

    const focusTableCellValidationErrorInput = async (errOverride) => {
      const err = errOverride || findFirstTableCellError()
      if (!err) return false

      pendingTableCellErrorFocusKey.value = getTableCellInputKey(err.fieldId, err.dataIndex)
      await nextTick()
      await nextTick()

      const input = await waitForTableCellErrorInput(err)
      if (!input) {
        console.warn('[TableDataTable focus] input not found', err)
        return false
      }

      return tryFocusTableCellErrorInput(err)
    }

    const refocusTableCellValidationErrorInput = async () => {
      const pendingKey = pendingTableCellErrorFocusKey.value
      if (!pendingKey) return false
      const err = findFirstTableCellError()
      if (err) return tryFocusTableCellErrorInput(err)
      return tryFocusTableCellErrorInput(pendingKey)
    }

    expose({
      checkTableCellValidationError,
      findFirstTableCellError,
      focusTableCellValidationErrorInput,
      refocusTableCellValidationErrorInput,
    })

    return {
      isDev,
      isReleaseVer,
      searchState,
      showSearchText,
      fieldState,
      dataSource,
      tableColumns,
      onChange,
      onChangeColumnTitle,
      onSortPlatformColumn,
      onDeleteRow,
      numberTypeOptions,
      customRow,
      onMouseEnterDrag,
      getComponent,
      getComponentProps,
      tableRootRef,
      onAddColumn,
      onDeleteColumn,
      titleCase,
      getFieldStateKey,
      customizedVersionSelectedDocIdByFieldId,
      sortDocIds,
    }
  },
}
</script>

<style scoped>
.search-row {
  margin-bottom: 28px;
}

.search-input {
  width: 100%;
}

.object-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.switch-label {
  margin-left: 8px;
}

.add-column {
  display: flex;
  justify-content: flex-end;
  margin: 20px 0;
}

.delete-column-btn {
  color: #999;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.delete-column-btn:hover {
  color: #1890ff;
}

:deep(.ant-table .section-editor table) {
  width: auto;
}

:deep(.table-data-table .ant-table-row) {
  background-color: #ffffff;
}

.sort-icon {
  font-size: 16px;
  padding: 4px;
  color: #1890ff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sort-icon:hover {
  color: #fff;
  background-color: #1890ff;
  border-radius: 100%;
  box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.5);
}

.table-label {
  display: inline-block;
  color: rgba(0, 0, 0, 0.5);
  font-size: 12px;
  white-space: nowrap;
  min-width: 70px;
}

.docs-setting-desc-list {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  margin: 8px auto 0;
  list-style: '- ' ;
  padding-left: 12px;
  white-space: normal;
  word-break: break-word;
}

.docs-setting-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
}
</style>
